import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import type { CallAiInsightsModel } from '../dto/softphone/call-ai-insights.dto';
import { BaseEntity } from './base.entity';
import { CallUserEntity } from './call-user.entity';
import { UserEntity } from './user.entity';

@Index('call__fk_user__id_fk', ['fkUserId'], {})
@Index('call_externalId_uindex', ['externalId'], { unique: true })
@Index('call_callerIdMatch_idx', ['callerIdMatchType', 'callerIdMatchEntityId'])
@Index('call_repCallSid_idx', ['repCallSid'])
@Entity('call')
export class CallEntity extends BaseEntity {
	@Column('varchar', {
		name: 'externalId',
		nullable: true,
		unique: true,
		length: 64,
	})
	public externalId!: string | null;

	@Column('int', { name: '_fk_user__id', nullable: true, unsigned: true })
	public fkUserId!: number | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('varchar', { name: 'result', nullable: true, length: 255 })
	public result!: string | null;

	@Column('text', { name: 'notes', nullable: true })
	public notes!: string | null;

	@Column('text', { name: 'ai_summary', nullable: true })
	public aiSummary!: string | null;

	/**
	 * Structured post-call AI analysis (PD-14417): sentiment, key
	 * points, and detected commitments (wrap-up suggested actions).
	 * Written by the post-call processor alongside `aiSummary`; null
	 * for calls processed before this landed or when extraction
	 * fell back to plain text.
	 */
	@Column('json', { name: 'aiInsights', nullable: true })
	public aiInsights!: CallAiInsightsModel | null;

	/**
	 * Full text transcript of the call recording (DD.11.2). Produced
	 * by the post-call processing pipeline: Deepgram pre-recorded
	 * transcription on the Twilio recording bytes, written here when
	 * the job completes. Null when:
	 *   - Recording is off for the line, or
	 *   - The post-call job is still in flight, or
	 *   - The transcription failed (transcriptionStatus carries the
	 *     failure for retry / observability)
	 *
	 * `aiSummary` (above) is the LLM summary derived from this text;
	 * usually populated together a few seconds after the transcript
	 * lands.
	 */
	@Column('text', { name: 'transcriptText', nullable: true })
	public transcriptText!: string | null;

	/**
	 * Structured per-turn transcript (DD.12.0b). When the recording is
	 * dual-channel (`recordingChannels === 'dual'`), Deepgram returns
	 * per-channel utterances which we interleave chronologically and
	 * store here. Used by the Communications-tab call viewer to render
	 * the transcript as chat bubbles labelled by speaker.
	 *
	 * Each turn:
	 *   { channel: 0 | 1, startSec, endSec, text }
	 *
	 * `channel` is the speaker: 0 = the other party (customer / callee),
	 * 1 = the rep, for both call directions. Twilio puts the recorded
	 * leg on channel 0, which is the customer on inbound calls but the
	 * rep on outbound calls, so the post-call processor swaps outbound
	 * channels before storing them (PD-17007; rows written before that
	 * are not migrated). Speaker NAMES are NOT stored here; they're resolved
	 * at render time by joining `fkUser` (rep) and the matched
	 * client/vendor (caller). Storing names would invalidate on rep
	 * renames or contact merges.
	 *
	 * Null on:
	 *   - Pre-DD.12.0b calls (forward-only — no backfill)
	 *   - Mono / single-channel recordings (e.g. AI-agent recordings,
	 *     conference legs, legacy paths) — `transcriptText` still
	 *     populated, just no per-turn breakdown
	 *   - Calls where the post-call processor hasn't completed yet
	 */
	@Column('json', { name: 'transcriptTurns', nullable: true })
	public transcriptTurns!: Array<{
		channel: 0 | 1;
		startSec: number;
		endSec: number;
		text: string;
	}> | null;

	/**
	 * Free-form per-call extension (mirrors `chat_conversation.metadata`).
	 * Currently holds the AI↔human voice-handoff state — see
	 * `CallAiHandoffStateModel`: `aiPaused` (the AI runtime stops engaging
	 * the call when true) and `activeAiAgentId` (which AI agent currently
	 * owns the call). `VoiceHandoffService` is the single writer.
	 */
	@Column('json', { name: 'metadata', nullable: true })
	public metadata?: Record<string, unknown>;

	/**
	 * Tracks the post-call processing pipeline status. `null` ==
	 * untouched (no recording, or recording happened pre-feature).
	 * Other states drive UI affordances (loading vs available vs
	 * retry button) and let the processor short-circuit on retry.
	 */
	@Column('varchar', {
		name: 'transcriptionStatus',
		nullable: true,
		length: 32,
	})
	public transcriptionStatus!:
		| 'queued'
		| 'transcribing'
		| 'summarizing'
		| 'completed'
		| 'failed'
		| null;

	/**
	 * Caller-ID match recorded at TwiML-routing time for inbound
	 * softphone calls. Null on outbound calls and on legacy imported
	 * rows. Populated from `CallerIdMatchResultDto.best`.
	 */
	@Column('varchar', {
		name: 'callerIdMatchType',
		nullable: true,
		length: 16,
	})
	public callerIdMatchType!: 'client' | 'vendor' | null;

	@Column('int', {
		name: 'callerIdMatchEntityId',
		nullable: true,
		unsigned: true,
	})
	public callerIdMatchEntityId!: number | null;

	/**
	 * Disposition columns populated by the post-call wrap-up flow.
	 * The rep picks a code from a short preset list (or types a
	 * custom one) plus optional free-form notes; the panel can also
	 * flag a follow-up commitment with a date.
	 *
	 * `result` (existing column) carries the call-lifecycle outcome
	 * stamped by the status webhook (`completed`, `no-answer`, etc.) —
	 * `dispositionCode` is the rep's BUSINESS verdict on what
	 * happened ("Spoke to customer", "Wrong number", "Follow-up
	 * needed"). Don't conflate them.
	 */
	@Column('varchar', {
		name: 'dispositionCode',
		nullable: true,
		length: 64,
	})
	public dispositionCode!: string | null;

	@Column('text', { name: 'dispositionNote', nullable: true })
	public dispositionNote!: string | null;

	@Column({
		type: 'boolean',
		name: 'requiresFollowUp',
		width: 1,
		default: () => "'0'",
	})
	public requiresFollowUp!: boolean;

	@Column('datetime', { name: 'followUpDate', nullable: true })
	public followUpDate!: Date | null;

	/**
	 * The follow-up TASK (`_event` row) created from this call's wrap-up
	 * (PD-14437). Doubles as the idempotency guard — a re-saved wrap-up
	 * won't create a second task while this is set — and gives
	 * re-attribution (PD-14419) a handle to retarget the task. The
	 * denormalized requiresFollowUp/followUpDate columns above stay
	 * maintained regardless (cheap earliest-follow-up list filters).
	 */
	@Column('int', {
		name: 'fk__event__id_followUp',
		nullable: true,
		unsigned: true,
	})
	public followUpEventId!: number | null;

	/**
	 * Twilio Recording metadata. Populated by the
	 * `recording-status` webhook when the recording finishes; null
	 * for calls that weren't recorded (tenant flag off) and for
	 * legacy imported call rows.
	 *
	 * `recordingUrl` is Twilio's CDN URL — we never expose it
	 * directly to the browser. The recording playback endpoint
	 * proxies authenticated reads through our master Twilio creds
	 * after a permission check (rep / manager / admin), so a leaked
	 * URL doesn't bypass authorization.
	 */
	@Column('varchar', {
		name: 'recordingSid',
		nullable: true,
		length: 64,
	})
	public recordingSid!: string | null;

	@Column('varchar', {
		name: 'recordingUrl',
		nullable: true,
		length: 512,
	})
	public recordingUrl!: string | null;

	@Column('int', {
		name: 'recordingDurationSeconds',
		nullable: true,
		unsigned: true,
	})
	public recordingDurationSeconds!: number | null;

	@Column('varchar', {
		name: 'recordingChannels',
		nullable: true,
		length: 8,
	})
	public recordingChannels!: string | null;

	/**
	 * Conference on demand — populated when a call moves from a direct
	 * two-party bridge (inbound `<Dial><Client>`, outbound
	 * `<Dial><Number>`) into a Twilio Conference room, the first time a
	 * feature needs one (hold, transfer, add-to-call, coaching). See
	 * `SoftphoneConferenceUpgradeService`.
	 *
	 * `conferenceName` is generated server-side once per call
	 * (`softphone-call-{dbCallId}`) and is the join key for both legs.
	 * On an outbound call it is stamped BEFORE the move as the "move in
	 * progress" marker: only the callee leg is redirected, and the rep's
	 * `<Dial>` action webhook follows it into the room when the name is
	 * set (PD-17007).
	 *
	 * `conferenceSid` is Twilio's identifier for the conference room,
	 * which only exists once participants have joined; it's set by the
	 * conference-status webhook or the upgrade itself.
	 *
	 * Both fields stay null on calls that never needed a conference.
	 */
	@Column('varchar', {
		name: 'conferenceName',
		nullable: true,
		length: 128,
	})
	public conferenceName!: string | null;

	@Column('varchar', {
		name: 'conferenceSid',
		nullable: true,
		length: 64,
	})
	public conferenceSid!: string | null;

	/**
	 * Inbound only — the rep's Twilio CallSid: the `<Client>` leg of the
	 * customer's `<Dial>`, stamped when the rep's browser confirms it
	 * answered (`confirm-rep-leg`). Null until then, so a set value also
	 * means "the rep answered".
	 *
	 * Kept distinct from `externalId` (which is the customer's CallSid
	 * on an inbound call) because the two legs have separate
	 * lifecycles, and a transfer adds NEW participant CallSids that
	 * aren't either of these. Outbound calls leave it null: their rep
	 * leg IS `externalId`.
	 */
	@Column('varchar', {
		name: 'repCallSid',
		nullable: true,
		length: 64,
	})
	public repCallSid!: string | null;

	/**
	 * Outbound only — the CallSid of the callee leg: the `<Number>` of
	 * the rep's `<Dial>`, stamped from that leg's first status callback
	 * (PD-17007). Counterpart to `repCallSid` for outbound calls: hold,
	 * transfer, AI hand-off and voicemail drop act on the OTHER party,
	 * and for outbound that is this leg, not `externalId` (the rep's
	 * browser call). Set while the callee is still ringing, so it does
	 * not by itself mean the call was answered.
	 *
	 * Stays null for inbound calls; those use `externalId` directly
	 * since that's already the customer's leg.
	 */
	@Column('varchar', {
		name: 'targetCallSid',
		nullable: true,
		length: 64,
	})
	public targetCallSid!: string | null;

	/**
	 * True when the customer was routed to voicemail instead of being
	 * connected to a rep. Set when the rep doesn't answer (rep-leg
	 * status webhook fires `no-answer`/`canceled`/`failed`/`busy`)
	 * and we redirect the customer's leg to a `<Record>` TwiML.
	 *
	 * The recording itself lands on the existing `recordingSid` /
	 * `recordingUrl` columns via the same recording-status webhook
	 * the in-call recordings use — voicemail is just a recording with
	 * `isVoicemail=true`. Inbox UI can filter on this to surface
	 * voicemails as their own queue.
	 */
	@Column({
		type: 'boolean',
		name: 'isVoicemail',
		width: 1,
		default: () => "'0'",
	})
	public isVoicemail!: boolean;

	/**
	 * The tenant-owned phone number the customer dialed (`body.To`
	 * from the inbound webhook), in E.164. Persisted on inbound call
	 * creation so downstream resolvers (DD.4.F no-answer fallback)
	 * can look up the `SoftphoneNumberEntity` → `SoftphoneNumberConfigEntity`
	 * chain that owns this call without re-querying Twilio.
	 *
	 * Null on legacy rows (pre-DD.4.F) and on outbound calls (where
	 * the "dialed-in" concept doesn't apply).
	 */
	@Column('varchar', {
		name: 'phoneTo',
		nullable: true,
		length: 32,
	})
	public phoneTo!: string | null;

	@ManyToOne(() => UserEntity, (user) => user.calls, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;

	@OneToMany(() => CallUserEntity, (callUser) => callUser.fkCall, {})
	public callUsers!: Array<CallUserEntity>;
}
